import apiClient from "../../api/api";

/**
 * Retrieves the diary (time tracking records) of a specific user for a given date.
 *
 * @async
 * @function getUserDiaryRequest
 * @param {number|string} userId - Unique identifier of the user.
 * @param {string} date - Date used to filter the diary entries (format: YYYY-MM-DD).
 * @param {number} timezoneOffset - Client timezone offset in minutes (e.g. -180 for UTC-3).
 * @returns {Promise<Object>} User diary data returned by the API.
 *
 * @example
 * await getUserDiaryRequest(1097, "2025-12-15", -180);
 */
export const getUserDiaryRequest = async(userId, date, timezoneOffset) => {
    const getUserDiaryResponse = await apiClient.get(import.meta.env.VITE_GET_USER_DIARY+`/${userId}/diary?date=${date}&timezoneOffset=${timezoneOffset}`, {timeout:150000})
    
    return getUserDiaryResponse.data
}


/**
 * Retrieves a screenshot or webcam image for a specific user.
 *
 * @async
 * @function requestGetImagesFromBucket
 * @param {number|string} userId - Unique identifier of the user.
 * @param {string} imagePath - Path of the image to be retrieved.
 * @param {"screenshot"|"webcam"} type - Type of image to retrieve.
 * @returns {Promise<Object|Blob>} Image data returned by the API.
 *
 * @example
 * await requestGetImagesFromBucket(1097, "/images/2025/12/shot.png", "screenshot");
 */
export const requestGetImagesFromBucket = async(userId, imagePath, type) => {
    const getImageResponse = await apiClient.get(import.meta.env.VITE_GET_USER_DIARY+`/${userId}/images?imagePath=${imagePath}&type=${type}`, {responseType: "blob", timeout: "90000"})
    
    return getImageResponse.data
}

/**
 * Deletes one or more time cards for a specific user.
 *
 * @async
 * @function deleteTimeCardsRequest
 * @param {number|string} userId - Unique identifier of the user.
 * @param {Array<Object>|Object} timeCards - Time card(s) to be deleted.
 * @returns {Promise<Object>} API response confirming deletion.
 *
 * @example
 * await deleteTimeCardsRequest(1097, [{ id: 123 }, { id: 456 }]);
 */
export const deleteTimeCardsRequest = async(userId, timeCards) => {
    const deleteCardsResponse = await apiClient.delete(import.meta.env.VITE_GET_USER_DIARY+`/${userId}/cards`, { data:timeCards })
    
    return deleteCardsResponse.data
}

/**
 * Updates one or more time cards for a specific user.
 *
 * @async
 * @function updateTimeCardsRequest
 * @param {number|string} userId - Unique identifier of the user.
 * @param {Array<Object>|Object} timeCards - Time card(s) to be updated.
 * @returns {Promise<Object>} Updated time card data returned by the API.
 *
 * @example
 * await updateTimeCardsRequest(1097, [{ id: 123, startTime: "08:00" }]);
 */
export const updateTimeCardsRequest = async(userId, timeCards) => {
    const updateCardsResponse = await apiClient.put(import.meta.env.VITE_GET_USER_DIARY+`/${userId}/cards`, timeCards, {
        successToast: "Time cards updated successfully"
    })
    
    return updateCardsResponse.data
}

/**
 * @description Fetches the monthly tracking report based on the provided filter.
 * The report can be filtered either by manager or by project.
 * If a valid managerId is provided (greater than zero), the report will be filtered by manager.
 * Otherwise, it will be filtered by project.
 *
 * @async
 * @function requestGetTrackingMonthlyReport
 * @param {string} filterBy - Defines the filter criteria for the monthly report (e.g., month or period).
 * @param {number} managerId - The manager ID used to filter the report. Takes precedence if greater than zero.
 * @param {number} projectId - The project ID used to filter the report when managerId is not provided.
 *
 * @returns {Promise<any>} A promise that resolves with the monthly tracking report data.
 *
 * @throws {Error} Throws an error if the API request fails.
 */
export const requestGetTrackingMonthlyReport = async(filterBy, managerId, projectId) => {
    const params = {
        filterBy
    }

    if(managerId > 0){
        params.managerId = managerId
    }else{
        params.projectId = projectId
    }
    const getReportResponse = await apiClient.get(import.meta.env.VITE_GET_MONTHLY_REPORT, { params, timeout:90000 })
    
    return getReportResponse.data
}